"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CSRClient = void 0;
const commerce_core_1 = require("@broadleaf/commerce-core");
const types_1 = require("./types");
/**
 * Client used for interacting with the CSR related cart APIs.
 */
class CSRClient extends commerce_core_1.AbstractClient {
    constructor(options) {
        super({ ...options, basePath: options.basePath || '/api/cart-operations' });
    }
    /**
     * Cancel the provided CSR cart
     *
     * @param cartId the id of the cart to cancel
     * @param options the options to the HTTP call
     * @return a single cart
     */
    async cancelCSRCart(cartId, options) {
        const config = {
            method: 'delete',
            url: `/csr/cart/${cartId}`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Override the price of an item in the cart
     *
     * @param cartId the id of the cart
     * @param cartItemId the id of the cart item
     * @param overridePrice the override price details
     * @param options the options to the HTTP call
     * @return a single cart
     */
    async overrideItemPriceInCart(cartId, cartItemId, overridePrice, options) {
        const config = {
            method: 'patch',
            url: `/csr/cart/${cartId}/items/${cartItemId}/overridePrice`,
            data: overridePrice
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Override the price of a fulfillment group in the cart
     *
     * @param cartId the id of the cart
     * @param referenceNumber the reference number of the fulfillment group
     * @param overridePrice the override price details
     * @param options the options to the HTTP call
     * @return a single cart
     */
    async overrideFulfillmentGroupPriceInCart(cartId, referenceNumber, overridePrice, options) {
        const config = {
            method: 'patch',
            url: `/csr/cart/${cartId}/fulfillment-groups/${referenceNumber}/overrideFulfillmentPrice`,
            data: overridePrice
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        this.attachPriceParamIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Creates a new transfer token that can be exchanged for proof of access to transfer a particular cart.
     *
     * @param cartId the id of the cart to transfer
     * @param options the options to the HTTP call
     * @return a transfer token
     */
    async createTransferToken(cartId, options) {
        const config = {
            method: 'post',
            url: `/csr/cart-transfer/${cartId}/create-token`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Transfer the provided anonymous CSR cart to be in-process for the provided customer.
     *
     * @param cartId the id of the cart
     * @param token the transfer token
     * @param options the options to the HTTP call
     * @return a single cart transfered to the authenticated customer
     */
    async consumeTransferToken(cartId, token, options) {
        const config = {
            method: 'post',
            url: `/csr/cart-transfer/${cartId}/consume-token`,
            params: {
                transferCartToken: token
            }
        };
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Clone the customer's in-process cart into a new csr-owned cart.
     *
     * @param cartId the id of the cart
     * @param options the options to the HTTP call
     * @return a single cart transfered to the authenticated customer
     */
    async cloneCSRCartFromCustomer(cartId, options) {
        const config = {
            method: 'post',
            url: `/csr/cart-transfer/${cartId}/from-customer`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Transfer the provided CSR cart to be in-process for its customer.
     *
     * @param cartId the id of the cart
     * @param options the options to the HTTP call
     * @return a single cart transfered to the authenticated customer
     */
    async transferCSRCartToCustomer(cartId, options) {
        const config = {
            method: 'post',
            url: `/csr/cart-transfer/${cartId}/to-customer`
        };
        this.attachCartVersionHeaderIfPresent(config, options);
        const response = await this.call(config, options);
        return response?.data;
    }
    /**
     * Attaches the cart version header to the request `config` if present on `options`.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachCartVersionHeaderIfPresent(config, options) {
        if (options?.version !== undefined) {
            if (!config.headers) {
                config.headers = {};
            }
            config.headers[types_1.CartRequestHeaderType.CART_VERSION] = String(options.version);
        }
    }
    /**
     * Attaches the price flag param to the request `config` if present on `options`. This indicates that the cart should be repriced.
     *
     * @param config The axios request config to modify
     * @param options Options specifying additional parameters to be used to modify the `config`
     */
    attachPriceParamIfPresent(config, options) {
        if (options?.price !== undefined) {
            if (!config.params) {
                config.params = {};
            }
            config.params.price = options.price;
        }
    }
}
exports.CSRClient = CSRClient;
//# sourceMappingURL=csr-client.js.map